import React, { useState } from 'react';
import './SwaraDharmaDisplay.css';

/**
 * Maps a swaraIdentity string to a CSS class name.
 * Svarita variants (e.g. "जात्य-स्वरित", "क्षैप्र-स्वरित") all map to swara-svarita.
 */
const getSwaraClass = (identity) => {
  if (!identity) return 'swara-udatta';
  const id = identity.trim();

  if (id === 'दीर्घस्वरित' || id === 'दीर्घ-स्वरित') return 'swara-deergha-svarita';
  if (id.includes('स्वरित') || id.toLowerCase().includes('svarita')) return 'swara-svarita';
  if (id === 'अनुदात्त' || id.toLowerCase() === 'anudatta') return 'swara-anudatta';
  if (id === 'प्रचय' || id.toLowerCase() === 'prachaya') return 'swara-prachaya';

  // Default — उदात्त or unrecognized
  return 'swara-udatta';
};

const SwaraDharmaTooltip = ({ record }) => {
  const rows = [];

  if (record.swaraIdentity) {
    rows.push({ label: 'स्वरः', value: record.swaraIdentity });
  }
  if (record.swaritaGenesis) {
    rows.push({ label: 'स्वरित-प्रकारः', value: record.swaritaGenesis });
  }
  if (record.duration) {
    rows.push({ label: 'दीर्घता', value: record.duration });
  }
  if (record.underlyingState) {
    rows.push({ label: 'मूल-स्वरः', value: record.underlyingState });
  }
  if (record.bearer) {
    rows.push({ label: 'वाहकः', value: record.bearer });
  }

  if (rows.length === 0) return null;

  return (
    <div className="swara-dharma-tooltip">
      {rows.map((r, i) => (
        <div key={i} className="swara-dharma-tooltip-row">
          <span className="swara-dharma-tooltip-label">{r.label}:</span>
          <span className="swara-dharma-tooltip-value">{r.value}</span>
        </div>
      ))}
    </div>
  );
};

const SyllableSpan = ({ record }) => {
  const [hovered, setHovered] = useState(false);
  const cssClass = getSwaraClass(record.swaraIdentity);
  // Display text as-is from API - VijayaDV uses Private Use Area characters
  // for Vedic accents, not standard Unicode combining marks
  const displayText = record.text || '';

  return (
    <span
      className={`swara-dharma-syllable ${cssClass}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {displayText}
      {hovered && <SwaraDharmaTooltip record={record} />}
    </span>
  );
};

const SwaraDharmaLine = ({ records }) => {
  if (!records || records.length === 0) return null;

  return (
    <div className="swara-dharma-line">
      {records.map((rec, i) => (
        <SyllableSpan key={i} record={rec} />
      ))}
    </div>
  );
};

const SwaraDharmaLegend = () => (
  <div className="swara-dharma-legend">
    <span className="swara-dharma-legend-item">
      <span className="swara-dharma-legend-swatch legend-udatta">अ</span>
      उदात्त
    </span>
    <span className="swara-dharma-legend-item">
      <span className="swara-dharma-legend-swatch legend-anudatta">अ</span>
      अनुदात्त
    </span>
    <span className="swara-dharma-legend-item">
      <span className="swara-dharma-legend-swatch legend-svarita">अ॑</span>
      स्वरित
    </span>
    <span className="swara-dharma-legend-item">
      <span className="swara-dharma-legend-swatch legend-prachaya">अ</span>
      प्रचय
    </span>
    <span className="swara-dharma-legend-item">
      <span className="swara-dharma-legend-swatch legend-deergha">अ</span>
      दीर्घस्वरित
    </span>
  </div>
);

/**
 * SwaraDharmaDisplay — shared panel for per-syllable Vedic accent analysis.
 *
 * Props:
 *   inputSwaraDharma  — SwaraDharmaRecord[] for input text
 *   outputSwaraDharma — SwaraDharmaRecord[] (flat) or SwaraDharmaRecord[][] (nested per-line)
 *   outputIsNested    — boolean: true for VikrutiPaatham (per-line), false for Sandhi (flat)
 */
const SwaraDharmaDisplay = ({ inputSwaraDharma, outputSwaraDharma, outputIsNested }) => {
  const hasInput = inputSwaraDharma && inputSwaraDharma.length > 0;
  const hasOutput = outputSwaraDharma &&
    (Array.isArray(outputSwaraDharma) ? outputSwaraDharma.length > 0 : false);

  if (!hasInput && !hasOutput) return null;

  return (
    <div className="swara-dharma-panel">
      <SwaraDharmaLegend />

      {hasInput && (
        <>
          <div className="swara-dharma-section-label">निवेश-स्वरधर्मः (Input Swara Dharma)</div>
          <SwaraDharmaLine records={inputSwaraDharma} />
        </>
      )}

      {hasOutput && (
        <>
          <div className="swara-dharma-section-label">निर्गम-स्वरधर्मः (Output Swara Dharma)</div>
          {outputIsNested
            ? outputSwaraDharma.map((lineRecords, idx) => (
                <SwaraDharmaLine key={idx} records={lineRecords} />
              ))
            : <SwaraDharmaLine records={outputSwaraDharma} />
          }
        </>
      )}
    </div>
  );
};

export default SwaraDharmaDisplay;
